import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Document } from '../types';
import { documentsApi } from '../store/api/documentsApi';
import type { AppDispatch } from '../store';

export function useApplicationRouteDocuments(routeIds: number[]) {
  const dispatch = useDispatch<AppDispatch>();
  const [routeDocumentsByRouteId, setRouteDocumentsByRouteId] = useState<Map<number, Document[]>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [fetchVersion, setFetchVersion] = useState(0);

  const sortedRouteIds = useMemo(
    () => routeIds.slice().sort((a, b) => a - b),
    [routeIds]
  );

  const refetchRouteDocuments = useCallback(() => {
    setFetchVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!sortedRouteIds.length) {
      setRouteDocumentsByRouteId(new Map());
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    Promise.all(
      sortedRouteIds.map((routeId) =>
        dispatch(documentsApi.endpoints.getDocumentsByRoute.initiate(routeId, { forceRefetch: true })).unwrap()
      )
    )
      .then((results) => {
        if (cancelled) return;
        const next = new Map<number, Document[]>();
        sortedRouteIds.forEach((routeId, index) => {
          next.set(routeId, results[index] ?? []);
        });
        setRouteDocumentsByRouteId(next);
      })
      .catch(() => {
        if (!cancelled) setRouteDocumentsByRouteId(new Map());
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, sortedRouteIds, fetchVersion]);

  return { routeDocumentsByRouteId, isLoading, refetchRouteDocuments };
}
